import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, inr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Lock } from "lucide-react";

export default function Checkout() {
  const { orderId } = useParams();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => { if (!loading && !user) nav("/signin"); }, [user, loading, nav]);
  useEffect(() => {
    api.get("/orders/mine", { params: { role: "buyer" } }).then(r => {
      const o = r.data.find(x => x.id === orderId);
      if (!o) { toast.error("Order not found"); nav("/buyer"); return; }
      setOrder(o);
    });
  }, [orderId, nav]);

  const pay = async () => {
    setPaying(true);
    try {
      await api.post(`/orders/${orderId}/pay`);
      toast.success("Payment successful — seller notified");
      nav("/buyer");
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment failed"); }
    finally { setPaying(false); }
  };

  if (!order) return <div className="max-w-3xl mx-auto py-16 text-center text-slate-500">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-10">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Secure Checkout</div>
      <h1 className="text-3xl md:text-4xl font-extrabold mb-6">Complete your purchase</h1>

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
        <div className="flex items-center gap-4">
          {order.listing_photo && <img src={order.listing_photo} className="h-20 w-20 rounded-lg object-cover" alt="" />}
          <div>
            <div className="text-xs text-amber-600 uppercase font-bold">Won auction</div>
            <div className="font-bold">{order.listing_title}</div>
            <div className="text-xs text-slate-500">Sold by {order.seller_name}</div>
          </div>
        </div>

        <div className="mt-6 space-y-2 text-sm">
          <Row k="Winning bid" v={inr(order.winning_bid)} />
          <Row k={`Buyer Protection${order.buyer_fee_pct != null ? ` (${order.buyer_fee_pct}%)` : ""}`} v={inr(order.buyer_fee)} sub="Escrow + inspection + disputes" />
          <Row k="Shipping" v={inr(order.shipping)} />
          <Row k="Taxes" v={inr(order.tax)} />
          <div className="border-t border-slate-200 dark:border-slate-700 pt-3 mt-3 flex justify-between font-extrabold text-lg">
            <span>Total</span><span data-testid="checkout-total">{inr(order.buyer_total)}</span>
          </div>
        </div>

        <div className="mt-6 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 p-4 text-xs text-emerald-800 dark:text-emerald-300 flex gap-2">
          <ShieldCheck className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>Funds are held in AuctionBulls escrow. Released to seller only after 3-day inspection window.</div>
        </div>

        <Button data-testid="checkout-pay-now-button" onClick={pay} disabled={paying} className="mt-6 w-full h-12 bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold text-base rounded-xl">
          <Lock className="h-4 w-4 mr-2" /> {paying ? "Processing..." : `Pay ${inr(order.buyer_total)} securely`}
        </Button>
        <div className="text-[11px] text-slate-500 text-center mt-2">MOCKED PAYMENT — simulates a marketplace payment provider for MVP.</div>
      </div>
    </div>
  );
}
function Row({ k, v, sub }) {
  return (
    <div className="flex justify-between items-start">
      <div>
        <div className="text-slate-600 dark:text-slate-400">{k}</div>
        {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
      </div>
      <div className="font-semibold">{v}</div>
    </div>
  );
}
